import { usePostJwtRefreshToken } from '@framework/api-client';
import type { PersonaGiuridicaDTO } from '@framework/api-client';
import { AuthActions } from '@framework/auth';
import { useCallback } from 'react';
import { useQueryClient } from 'react-query';
import { useDispatch } from 'react-redux';

export interface UseLegalEntityIdentityActionsResult {
  switchToSelf: () => Promise<void>;
  switchToLegalEntity: (legalEntity: PersonaGiuridicaDTO) => Promise<void>;
  resetProfileSelection: () => void;
  returnToSelection: () => Promise<void>;
  isReturningToSelection: boolean;
}

export function useLegalEntityIdentityActions(): UseLegalEntityIdentityActionsResult {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const refreshToken = usePostJwtRefreshToken();

  const applyRefreshToken = useCallback(
    (result: {
      accessToken?: string | null;
      refreshToken?: string | null;
      legalEntityId?: string | null;
    }) => {
      dispatch(
        AuthActions.tokenRefreshed({
          accessToken: result.accessToken!,
          refreshToken: result.refreshToken!,
          legalEntityId: result.legalEntityId ?? null,
        }),
      );
      // Una scelta conclude l'eventuale re-selezione in corso.
      dispatch(AuthActions.clearReselection());
      queryClient.clear();
      // `tokenRefreshed` non transita `isLogged`, quindi forziamo il
      // ricaricamento dell'utente: `currentLoaded` ricalcola `isLogged` dal
      // nuovo `profiloSelezionato`.
      dispatch(AuthActions.reloadCurrent());
    },
    [dispatch, queryClient],
  );

  const switchToSelf = useCallback(async () => {
    // Il backend valorizza `profiloSelezionato.tipo = 'PF'`: al successivo
    // caricamento utente `isLogged` diventa true.
    const result = await refreshToken.mutateAsync({ data: {} });
    applyRefreshToken({ ...result, legalEntityId: null });
  }, [applyRefreshToken, refreshToken]);

  const switchToLegalEntity = useCallback(
    async (legalEntity: PersonaGiuridicaDTO) => {
      if (!legalEntity.id) return;

      const result = await refreshToken.mutateAsync({
        data: { idPersonaGiuridica: legalEntity.id },
      });
      applyRefreshToken({
        ...result,
        legalEntityId: legalEntity.id,
      });
    },
    [applyRefreshToken, refreshToken],
  );

  const resetProfileSelection = useCallback(() => {
    queryClient.clear();
  }, [queryClient]);

  const returnToSelection = useCallback(async () => {
    // Riapre subito la schermata "Accedi come" lato client (isLogged=false),
    // senza dipendere dal backend: il cambio profilo effettivo avviene quando
    // l'utente sceglie (switchToSelf/switchToLegalEntity).
    dispatch(AuthActions.requestReselection());
  }, [dispatch]);

  return {
    switchToSelf,
    switchToLegalEntity,
    resetProfileSelection,
    returnToSelection,
    isReturningToSelection: false,
  };
}
